import React from 'react'
import { AutomationTarget } from '../../../types'
import Money, { TextSize, TextWeight } from '../money'
import Text from '../text'
import Icon, { IconProps } from '../icon'
import PopupTooltip from '../../overlays/popupTooltip'
import classnames from 'classnames/bind'
import { match } from 'ts-pattern'
import styles from './styles.scss'
import { InfoPillProps } from '../infoPill'
import { ColorNames } from '../../../constants'
const cx = classnames.bind(styles) // eslint-disable-line

const Tooltip = ({
  content,
  iconStyle,
}: {
  content: React.ReactNode
  iconStyle: IconProps['iconStyle']
}) => (
  // @ts-ignore next
  <PopupTooltip
    content={content}
    wide={false}
    trigger={
      <Icon
        size='small'
        color='gray'
        name='info-circle'
        fitted
        style={{ verticalAlign: 'super' }}
        iconStyle={iconStyle}
      />
    }
  />
)

type LevelProps = {
  color: string
  size: TextSize
  weight: TextWeight
}

type LabelValueLevelProps = {
  label: LevelProps
  value: LevelProps
}

export interface LabelValueProps extends AutomationTarget {
  value: string | number
  label: string
  href?: string
  level?: 1 | 2 | 3 | 4 | 5
  isMoneyValue?: boolean
  tooltipContent?: React.ReactNode
  tooltipIconStyle?: IconProps['iconStyle']
  classNames?: { label?: string; value?: string }
  infoPill?: InfoPillProps
}
const LabelValue = ({
  value,
  label,
  level = 3,
  isMoneyValue,
  href,
  tooltipContent,
  tooltipIconStyle = 'solid',
  elmId = '',
  classNames = {},
  infoPill = undefined,
}: LabelValueProps) => {
  const props = match<number, LabelValueLevelProps>(level)
    .with(1, () => ({
      label: { color: 'grayMedium', size: 'large', weight: 'black' },
      value: { color: 'gray', size: 'massive', weight: 'black' },
    }))
    .with(2, () => ({
      label: { color: 'grayMedium', size: 'small', weight: 'heavy' },
      value: { color: 'gray', size: 'big', weight: 'black' },
    }))
    .with(3, () => ({
      label: { color: 'gray', size: 'small', weight: 'normal' },
      value: { color: 'gray', size: 'small', weight: 'black' },
    }))
    .with(4, () => ({
      label: { color: 'grayMedium', size: 'tiny', weight: 'normal' },
      value: { color: 'gray', size: 'tiny', weight: 'black' },
    }))
    .with(5, () => ({
      label: { color: 'grayMedium', size: 'small', weight: 'normal' },
      value: { color: 'gray', size: 'big', weight: 'black' },
    }))
    // 3 is default
    .otherwise(() => ({
      label: { color: 'gray', size: 'small', weight: 'normal' },
      value: { color: 'gray', size: 'small', weight: 'black' },
    }))

  let specialLabel = isMoneyValue ? (
    <Money
      elmId={elmId ? `${elmId}-value` : 'value'}
      className={cx('value', `value-${level}`, classNames.value)}
      amount={typeof value === 'string' ? parseFloat(value) : value}
      {...props.value}
      infoPill={infoPill}
      overrides={{
        minimumFractionDigits: 2,
      }}
    />
  ) : null

  if (href) {
    specialLabel = (
      <a href={href} target='_blank'>
        <Text
          elmId={elmId ? `${elmId}-value` : 'value'}
          weight='black'
          color={ColorNames.Info}
          className={cx('link-value')}
        >
          {value}
        </Text>
      </a>
    )
  }

  return (
    <div>
      <Text
        {...props.label}
        elmId={elmId ? `${elmId}-label` : 'label'}
        className={cx('label', `label-${level}`, classNames.label)}
      >
        {label}
        {tooltipContent && (
          <span className='u-ml-1'>
            <Tooltip content={tooltipContent} iconStyle={tooltipIconStyle} />
          </span>
        )}
      </Text>
      {specialLabel ?? (
        <Text
          {...props.value}
          elmId={elmId ? `${elmId}-value` : 'value'}
          className={cx('value', `value-${level}`, classNames.value)}
        >
          {value}
        </Text>
      )}
    </div>
  )
}

export default LabelValue
